import { AlertTriangle, LoaderCircle, Plus } from "lucide-react";
import type { ReactNode } from "react";
import { useLocale } from "../i18n.js";
import { consoleText, formatCode, localizedError } from "../console-i18n.js";

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {action}
    </header>
  );
}

export function LoadingBlock() {
  const { t } = useLocale();
  return (
    <div className="state-block">
      <LoaderCircle className="spin" />
      <span>{t("loading")}</span>
    </div>
  );
}

export function ErrorBlock({ error, retry }: { error: unknown; retry?: () => void }) {
  const { locale, t } = useLocale();
  return (
    <div className="state-block error-state">
      <AlertTriangle />
      <span>{localizedError(locale, error, consoleText(locale).common.requestFailed)}</span>
      {retry ? (
        <button className="secondary-button" type="button" onClick={retry}>
          {t("retry")}
        </button>
      ) : null}
    </div>
  );
}

export function EmptyBlock({ label }: { label?: string }) {
  const { t } = useLocale();
  return (
    <div className="state-block">
      <span>{label ?? t("noData")}</span>
    </div>
  );
}

export function CreateButton({ onClick, label }: { onClick: () => void; label?: string }) {
  const { t } = useLocale();
  return (
    <button className="primary-button" type="button" onClick={onClick}>
      <Plus size={16} />
      {label ?? t("create")}
    </button>
  );
}

export function RiskBadge({ level }: { level: string }) {
  const { locale } = useLocale();
  return (
    <span className={`risk-label risk-${level.toLowerCase()}`}>{formatCode(locale, level)}</span>
  );
}
